import { Messages } from "@arcware-cloud/shared-pixelstreaming-websdk";
import {
  AggregatedStats,
  BaseMessage,
  Flags,
  Logger,
  NumericParameters,
  PixelStreaming,
  WebSocketTransport,
  PixelStreamingOverrides,
} from "@epicgames-ps/lib-pixelstreamingfrontend-ue5.5";

import { z, ZodType } from "zod";
import { ArcwareConfig } from "./ArcwareConfig";
import { ApplyUrlHack } from "./ApplyUrlHack";
import { EventHandler } from "./domain/EventHandler";
import { Session } from "./domain/Session";
import { Stats } from "./domain/Stats";
import debounce from "./domain/debounce";
import { LoveLetters } from "./ui/LoveLetters";
import { ArcwareLogoLoader } from "./ui/ArcwareLogoLoader";
import { MicrophoneOverlay } from "./ui/MicrophoneOverlay";
import { ConnectionIdentifier, WebsocketState } from "./domain/ConnectionIdentifier";

export class ArcwarePixelStreaming extends PixelStreaming {
  /** Override default config with ArcwareConfig. */
  public override config: ArcwareConfig;
  public streamInfo?: Messages.StreamInfo;
  public loveLettersContainer: HTMLDivElement | undefined;
  private loveLettersList: string[];
  private loveLettersQueue: string[] = [];
  private isProcessingQueue: boolean = false;
  private microphoneOverlay: MicrophoneOverlay;

  /** Returns a list of WebSocketStates of all PixelStreaming Instances generated. */
  public get WebsocketStates() {
    return ConnectionIdentifier.Instance.GetWebSocketStates();
  }

  /** Counts all active PixelStreaming Instances generated. (CONNECTING & CONNECTED) */
  public get ActiveInstances() {
    return ConnectionIdentifier.Instance.ActiveInstances;
  }

  /** Returns this PixelStreaming Instances websocket state. */
  public get websocketState(): WebsocketState {
    let state: any = (this.webRtcController?.transport as WebSocketTransport)?.webSocket?.readyState;
    return state !== undefined ? (state as WebsocketState) : WebSocket.CLOSED;
  }

  constructor(config: ArcwareConfig, overrides?: PixelStreamingOverrides) {
    /** As soon as upstream is fixed, we got to adjust the url building process. */
    ApplyUrlHack();

    super(config, overrides);

    /** Theoretically this call should be used to implement the signalling url, but this would not work at all, if config is setup for "AutoConnect=true"
     * Instead we use ApplyUrlHack();
     */
    // this.setSignallingUrlBuilder(() => {
    //   const regex = /(?<=\?.*)\?/g;
    //   const signallingServerUrl = this.config.getTextSettingValue(TextParameters.SignallingServerUrl);
    //   return signallingServerUrl + "?" + this.config.urlFlags;
    // });

    // Set override config.
    this.config = config;
    this.loveLettersList = [];
    this.microphoneOverlay = new MicrophoneOverlay(this);

    // this.loveLettersContainer = null;

    this.wrapWebSocketOnCloseHandler();

    // Bind the event listener function to the class instance
    this.handleResolutionChange = this.handleResolutionChange.bind(this);

    // Register message handlers.
    this.addMessageHandler("letter", Messages.ZLoveLetter, this.onLoveLetter);
    this.addMessageHandler("streamInfo", Messages.ZStreamInfo, this.onStreamInfo);
    this.addMessageHandler("sessionId", Messages.ZSessionId, this.onSessionId);
    this.addMessageHandler("error", Messages.ZErrorMessage, this.onError);
    this.addMessageHandler("ping", Messages.ZPing, this.onPing);
    this.addMessageHandler("queue", Messages.ZQueue, this.onQueue);
    this.addMessageHandler("version", Messages.ZVersion, this.onVersion);

    // Create a debounced version of the handleResolutionChange function
    const debouncedResolutionChange = debounce(() => {
      this.handleResolutionChange();
    }, 500);

    // Register event listeners.
    this.addEventListener("videoInitialized", this.onVideoInitialized.bind(this));
    this.addEventListener("statsReceived", (event) => {
      this.sendStats(event.data.aggregatedStats);
    });

    // Add the window resize event listener
    window.addEventListener("resize", debouncedResolutionChange);

    this.webRtcController.streamMessageController.registerMessageHandler(
      0, // MessageDirection.ToStreamer,
      "TextboxEntry",
      (messageData?: unknown) => {
        console.error(`Incomplete handler: ToStreamer.TextboxEntry`, messageData);
      }
    );

    this.initLoveLettersContainer();
    this.createMicrophoneOverlay();

    // Set pre configured handlers.
    if (this.config.settings.errorHandler) this.errorHandler.add(this.config.settings.errorHandler);
    if (this.config.settings.queueHandler) this.queueHandler.add(this.config.settings.queueHandler);
    if (this.config.settings.sessionIdHandler) this.sessionIdHandler.add(this.config.settings.sessionIdHandler);
    if (this.config.settings.loveLetterHandler) this.loveLetterHandler.add(this.config.settings.loveLetterHandler);

    ConnectionIdentifier.Instance.register(this);

    this.removeXRIconIfDisabled();

    this.handleMouseLock();
  }

  /** Getter for the session object. */
  public get session(): Session {
    return this.config.session;
  }

  /**
   * * * * * * *
   *  Listen   *
   * * * * * * *
   */
  /** On version requested, the version of the WebSDK would be returned. */
  private onVersion(message: Messages.Version) {
    this.send({ type: "version", version: this.config.VERSION });
  }

  /** On ping the session creation timestamp will be updated. */
  private onPing(message: Messages.Ping) {
    if (this.config.isFlagEnabled("HoveringMouse") !== !this?.streamInfo?.streamInfo.meta?.mouseLock)
      this.handleMouseLock();
    if (this.session.current) this.session.set(this.session.current);
  }

  /** Handle incoming configurations. */
  // public readonly streamInfoHandler = new EventHandler<never>();
  private onStreamInfo(streamInfo: Messages.StreamInfo) {
    Logger.Info(`StreamInfo received.`);

    // Save the streamInfo to the instance variable
    this.streamInfo = streamInfo;

    // By setting this hidden flag, the evaluation of the streamInfo can be cut off.
    // Cutting of the streamInfo can lead to unwanted behavior, therefore it's not advertised.
    if (!(this.config?.settings as { "do-not-eval-streamInfo"?: true })?.["do-not-eval-streamInfo"]) {
      const { afk } = streamInfo.streamInfo;
      if (afk) {
        this.config.setFlagEnabled(Flags.AFKDetection, afk.enabled);
        this.config.setNumericSetting(NumericParameters.AFKTimeoutSecs, afk.warn + afk.error);
        // Hack
        const { afkController } = this.webRtcController;
        afkController.countDown = afk.action;
      }

      // Relevant settings need to be interpreted when they come through the streamInfo to the client.
      if (streamInfo.streamInfo.webSdkSettings?.init) {
        this.config.setSettings(streamInfo.streamInfo.webSdkSettings.init);
      }

      if (streamInfo.streamInfo.webSdkSettings?.conf) {
        Object.entries(streamInfo.streamInfo.webSdkSettings.conf).forEach(([key, value]) => {
          const k = key as keyof typeof this.config.settings;
          this.config.settings[k] = value as never;

          if (key === "connectionIdentifierLoggingDisabled" && value === true) ConnectionIdentifier.Instance.disable();
        });
      }
    }

    this.handleMouseLock();
    this.injectCustomUI();

    // EventHandler.Emit(this.streamInfoHandler, undefined as never);
  }

  // Externalized

  /** On ping the session creation timestamp will be updated. */
  public readonly queueHandler = new EventHandler<Messages.Queue>();
  private onQueue(message: Messages.Queue) {
    Logger.Info(`QueueInfo received.`);
    EventHandler.Emit(this.queueHandler, message);
  }

  /** Error receiver. */
  public readonly errorHandler = new EventHandler<Messages.ErrorMessage>();
  private onError(error: Messages.ErrorMessage) {
    Logger.Error(error.type);
    EventHandler.Emit(this.errorHandler, error);
  }

  /** LoveLetter */
  public readonly loveLetterHandler = new EventHandler<Messages.LoveLetter>();
  private onLoveLetter(loveLetter: Messages.LoveLetter) {
    // Logger.Info(Logger.GetStackTrace(), loveLetter.reason);
    if (this.config.settings.loveLetterLogging) console.info(loveLetter.reason);
    EventHandler.Emit(this.loveLetterHandler, loveLetter);
    this.pushLetter(loveLetter.reason);
  }

  /** SessionId */
  public readonly sessionIdHandler = new EventHandler<string>();
  private onSessionId(message: Messages.SessionId) {
    Logger.Info(message.sessionId);
    // console.info(`Session: ${message.sessionId}`);
    this.session.set(message.sessionId);

    EventHandler.Emit(this.sessionIdHandler, message.sessionId);
  }

  /** VideoInitialized */
  public readonly videoInitializedHandler = new EventHandler<never>();
  private onVideoInitialized(): void {
    /** The videoInitialized event is important for the Arcware Cloud BAckend, since without that event the instance will be cleaned up (killed). */
    this.handleMouseLock();
    this.send({ type: "onVideoInitialized" });
    EventHandler.Emit(this.videoInitializedHandler, undefined as never);
    this.handleResolutionChange();
    this.handleRemoveLoveLetters();
    this.microphoneOverlay.toggleVisibility(false);
    this.applyResolutionIfPlaying();
    this.removeXRIconIfDisabled();
  }

  // Internal
  /** WebSocket Close */
  public readonly websocketOnCloseHandler = new EventHandler<CloseEvent>();

  /** Adding a zod-safe handler. */
  private addMessageHandler<T extends { type?: string }>(
    type: string,
    zod: ZodType<T>,
    boundMethod: (this: this, payload: z.infer<typeof zod>) => void
  ) {
    this.signallingProtocol.addListener(type, (payload: string) => {
      const parsed = zod.safeParse(payload);
      if (parsed.success) boundMethod.call(this, parsed.data);
      else {
        // This cast has to be done, since Frontend does not use typescript-strict setting.
        const parsedError = parsed as unknown as z.SafeParseError<T>;
        const error = new Error(`Unexpected message content. Event:'${type}', ZodError: ${parsedError.error.message}`);
        Logger.Error(error.message);
      }
      if (type === "error") {
        this.disconnect();
      }
    });
  }

  /**
   * * * * * *
   *  Send   *
   * * * * * *
   */
  private sendStats(stats: AggregatedStats) {
    const message: Messages.Stats = { type: "stats", stats: Stats(stats) };
    // This cast has to be done, since Frontend does not use typescript-strict setting.
    this.send(message as { type: string });
  }

  private send<T extends { type: string }>(message: T): void;
  private send<K extends keyof typeof Messages.Send>(message: { type: K } & z.infer<(typeof Messages.Send)[K]>): void;
  private send<T extends { type: string }, K extends keyof typeof Messages.Send>(
    message: T | ({ type: K } & z.infer<(typeof Messages.Send)[K]>)
  ) {
    if (Messages.Send[message.type as K]) {
      const result = Messages.Send[message.type as K]!.safeParse(message);
      if (result.success) this.signallingProtocol.sendMessage(result.data as BaseMessage);
      else {
        // This cast has to be done, since Frontend does not use typescript-strict setting.
        const parsedError = result as unknown as z.SafeParseError<T>;
        Logger.Error(`Failed to send. ${parsedError.error}`);
      }
    } else {
      this.signallingProtocol.sendMessage(message);
    }
  }

  private handleResolutionChange() {
    // Get the resolution from the streamInfo if available
    if (!this?.streamInfo || !this?.webRtcController || !this?.webRtcController?.videoPlayer?.getVideoElement()) {
      return;
    }
    // Get the resolution from the streamInfo if available
    const resolution = this?.streamInfo?.streamInfo?.resolution;
    const ueVersion = this?.streamInfo?.streamInfo?.meta?.version ?? this.streamInfo?.streamInfo?.meta?.ueVersion;
    // Check if resolution.dynamic is true
    if (resolution && resolution?.dynamic) {
      setTimeout(() => {
        const { videoPlayer } = this?.webRtcController;
        const videoElementParent = videoPlayer?.getVideoParentElement();
        const browserWidth = videoElementParent?.getBoundingClientRect().width;
        const browserHeight = videoElementParent?.getBoundingClientRect().height;

        // Define maximum allowed resolution
        const maxWidth = resolution?.width;
        const maxHeight = resolution?.height;

        // Calculate the aspect ratio of the browser viewport
        const aspectRatio = browserWidth! / browserHeight!;

        // Calculate the new width and height based on the aspect ratio
        let limitedWidth, limitedHeight;
        if (aspectRatio > maxWidth / maxHeight) {
          // Width is the limiting factor
          limitedWidth = maxWidth;
          limitedHeight = maxWidth / aspectRatio;
        } else {
          // Height is the limiting factor
          limitedHeight = maxHeight;
          limitedWidth = maxHeight * aspectRatio;
        }

        if (videoPlayer) {
          // Check Unreal Engine version
          if (!ueVersion || ueVersion?.startsWith("4.27")) {
            // Use the aspect ratio limited resolution for the r.setres command
            const descriptor = { Console: `r.setres ${Math.round(limitedWidth)}x${Math.round(limitedHeight)}w` };
            this?.webRtcController?.emitUIInteraction(descriptor);
          } else {
            // Use the aspect ratio limited resolution for the onMatchViewportResolutionCallback
            videoPlayer.onMatchViewportResolutionCallback(Math.round(limitedWidth), Math.round(limitedHeight));
          }
        }
      }, 500);
    }
  }

  private applyResolutionIfPlaying() {
    const videoElement = this?.webRtcController?.videoPlayer.getVideoElement();
    if (videoElement) {
      videoElement.addEventListener("playing", () => {
        setTimeout(() => {
          this?.handleResolutionChange();
        }, 1500);
      });
    }
  }

  public removePlayer() {
    //this.signallingProtocol.disconnect();
    this.disconnect();
  }

  private handleMouseLock() {
    this?.config?.modifyInitialSettings(!this?.streamInfo?.streamInfo.meta?.mouseLock);
  }

  public initLoveLettersContainer() {
    const logoLoader = new ArcwareLogoLoader();
    if (!this.loveLettersContainer) {
      const loveLettersContainer = document?.createElement("div");
      const { videoPlayer } = this?.webRtcController;
      const videoElementParent = videoPlayer.getVideoParentElement();
      loveLettersContainer?.classList?.add("love-letters-box-root");
      videoElementParent?.appendChild(loveLettersContainer);
      const lettersBlock = document?.createElement("div");
      lettersBlock.id = "letters-block";
      const lettersWrapper = document?.createElement("div");
      lettersWrapper.id = "letters-wrapper";
      if (loveLettersContainer) {
        lettersBlock?.appendChild(logoLoader?.arcwareLogoLoader);
        loveLettersContainer?.appendChild(lettersBlock);
        lettersBlock?.appendChild(lettersWrapper);
      }

      lettersBlock.innerHTML = "";
      if (lettersBlock) {
        lettersBlock?.appendChild(logoLoader?.arcwareLogoLoader);
        lettersBlock?.appendChild(lettersWrapper);
      }
    }
  }

  private pushLetter(letter: string) {
    // Add the letter to the queue
    this?.loveLettersQueue?.push(letter);

    // If not already processing the queue, start processing
    if (!this.isProcessingQueue) {
      this?.processLoveLetterQueue();
    }
  }
  private processLoveLetterQueue() {
    // Set the flag to indicate that we are processing the queue
    this.isProcessingQueue = true;
    // Take the first item from the queue
    const letter = this.loveLettersQueue.shift();
    if (letter !== undefined) {
      const formattedLoveLetter: string = letter?.replace(/LL: |\.$/g, "");
      this?.loveLettersList?.push(formattedLoveLetter);
      const loveLettersBox = new LoveLetters();
      loveLettersBox?.addLetter(formattedLoveLetter, this?.loveLettersList?.length);
      // Process the next item in the queue after a delay
      setTimeout(() => {
        this.processLoveLetterQueue();
      }, 1000);
    } else {
      // If the queue is empty, reset the flag
      this.isProcessingQueue = false;
    }
  }

  private handleRemoveLoveLetters() {
    const loveLettersContainer = document?.querySelector(".love-letters-box-root");
    setTimeout(() => {
      if (loveLettersContainer) {
        loveLettersContainer?.classList?.add("fade-out");
      }
    }, 1000);
    setTimeout(() => {
      if (loveLettersContainer && loveLettersContainer?.parentNode) {
        loveLettersContainer?.parentNode?.removeChild(loveLettersContainer);
      }
    }, 1600);
  }

  public toggleAudio(videoElement: HTMLVideoElement, enabled: boolean) {
    const { streamController } = this.webRtcController;
    if (videoElement) {
      videoElement.muted = enabled;
      if (streamController) {
        streamController.audioElement.muted = enabled;
        if (videoElement?.currentTime > 0 && !videoElement?.paused && streamController?.audioElement?.paused)
          streamController?.audioElement?.play();
      }
    }
  }

  private createMicrophoneOverlay() {
    this?.microphoneOverlay?.createOverlay();
  }

  public toggleMic(enable: boolean, isDefault: boolean) {
    this?.config?.setFlagEnabled(Flags.UseMic, enable);
    if (!isDefault) {
      this?.microphoneOverlay?.toggleMessage(enable);
      setTimeout(() => {
        this?.microphoneOverlay?.toggleVisibility(true);
      }, 200);

      setTimeout(() => {
        this.reconnect();
      }, 1000);
    }
  }

  private wrapWebSocketOnCloseHandler() {
    let self = this;
    this.webRtcController?.transport?.addEventListener("close", (event) => {
      EventHandler.Emit(self.websocketOnCloseHandler, event as CloseEvent);
    });
  }

  public onStreamingStateChange(callback: (isStreaming: boolean) => void): void {
    // Listen to video element events
    const videoElement = this?.webRtcController?.videoPlayer?.getVideoElement();
    videoElement?.addEventListener("play", () => callback(true));
    videoElement?.addEventListener("pause", () => callback(false));
    videoElement?.addEventListener("ended", () => callback(false));

    // Listen to WebSocket events
    this.webRtcController?.transport.addEventListener("open", () => {
      callback(true);
    });
    this.webRtcController?.transport.addEventListener("close", () => {
      callback(false);
    });
  }

  private removeXRIconIfDisabled() {
    if (!this?.config?.initialSettings?.XRControllerInput) {
      this?.config?.setFlagEnabled(Flags.XRControllerInput, false);
      if (this.videoElementParent) {
        if (this?.videoElementParent?.parentElement) {
          const xrBtn = this?.videoElementParent?.parentElement.querySelector("#xrBtn");
          xrBtn?.remove();
        }
      }
    }
  }

  private injectCustomUI() {
    const customUI = document?.getElementById("stream-ui");
    const videoElementParent = this?.videoElementParent?.parentElement;
    if (customUI && videoElementParent) videoElementParent?.appendChild(customUI);
  }
}
